import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_VIRTUAL_FINISH_AT,
  PLAYER_COMMAND,
  PlayerCommand,
  playerOriginOf,
  readPlayerMessage,
  reachedVirtualFinish,
  shortcutFromKey,
  videoKeyOf,
} from './protocol';

/**
 * Player novo da Aprova Total Video Platform.
 *
 * Não é um <video> nem um player JS: é um app hospedado remotamente, carregado
 * dentro de um <iframe> 16:9. A `embedUrl` já vem do backend (no próprio
 * response da aula) com as credenciais temporárias no fragmento (#token=...).
 * O front apenas atribui a URL ao `src` — sem modificar, cachear ou logar.
 *
 * O iframe conversa com a página por postMessage (ver ./protocol.ts): é por
 * ele que a aula fica marcada como vista, o Game pontua, a linha do tempo da
 * aula leva o vídeo ao ponto e os atalhos de teclado funcionam com o foco na
 * página — o mesmo que os players antigos faziam por SDK.
 */

interface PlayerAprovaProps {
  embedUrl?: string;
  title?: string;
  className?: string;
  /** Segundos assistidos (posição no vídeo), lidos da telemetria do iframe. */
  onProgressSeconds?: (seconds: number) => void;
  /**
   * Conclusão: `virtual` ao passar de `virtualFinishAt` (%) e `ended` no fim
   * real do vídeo — o mesmo contrato do <Player> legado.
   */
  onFinish?: (origin?: 'virtual' | 'ended') => any;
  virtualFinishAt?: number;
  onVideoEnd?: () => void;
  /** Salto pedido pela página (ex.: tópicos da videoaula). */
  seekTo?: { seconds: number; requestId: number };
  /** Atalhos de teclado com o foco na página (padrão: ligados). */
  keyboardShortcuts?: boolean;
}

export default function PlayerAprova({
  embedUrl,
  title = 'Videoaula',
  className,
  onProgressSeconds,
  onFinish,
  virtualFinishAt = DEFAULT_VIRTUAL_FINISH_AT,
  onVideoEnd,
  seekTo,
  keyboardShortcuts = true,
}: PlayerAprovaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [loaded, setLoaded] = useState(false);

  const playerOrigin = playerOriginOf(embedUrl);
  const videoKey = videoKeyOf(embedUrl);

  // Os listeners são montados uma vez por vídeo; guardar os callbacks numa ref
  // evita reassinar a cada render e evita ficar com uma versão velha deles.
  const callbacksRef = useRef({
    onProgressSeconds,
    onFinish,
    onVideoEnd,
    virtualFinishAt,
  });
  callbacksRef.current = {
    onProgressSeconds,
    onFinish,
    onVideoEnd,
    virtualFinishAt,
  };

  // Estado da reprodução deste vídeo. A mesma aula recarregada (embedUrl nova,
  // mesmo vídeo) não zera: a conclusão não é registrada duas vezes.
  const playbackRef = useRef({
    ready: false,
    firedVirtual: false,
    firedEnded: false,
    pendingSeek: null as number | null,
  });

  useEffect(() => {
    playbackRef.current = {
      ready: false,
      firedVirtual: false,
      firedEnded: false,
      pendingSeek: null,
    };
  }, [videoKey]);

  const send = (command: PlayerCommand) => {
    const target = iframeRef.current?.contentWindow;
    if (!target || !playerOrigin) return;
    target.postMessage({ type: PLAYER_COMMAND, ...command }, playerOrigin);
  };
  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    if (!playerOrigin) return;

    const onMessage = (event: MessageEvent) => {
      // Segurança: só aceita mensagens vindas DESTE iframe e da origem dele.
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== playerOrigin) return;

      const message = readPlayerMessage(event.data);
      if (!message) return;

      const callbacks = callbacksRef.current;
      const playback = playbackRef.current;

      if (message.kind === 'telemetry') {
        const { playedSeconds } = message.snapshot;
        callbacks.onProgressSeconds &&
          callbacks.onProgressSeconds(Number(playedSeconds));

        if (
          !playback.firedVirtual &&
          reachedVirtualFinish(message.snapshot, callbacks.virtualFinishAt)
        ) {
          playback.firedVirtual = true;
          callbacks.onFinish && callbacks.onFinish('virtual');
        }
        return;
      }

      if (message.event === 'ready') {
        playback.ready = true;
        if (playback.pendingSeek !== null) {
          sendRef.current({ command: 'seek', seconds: playback.pendingSeek });
          playback.pendingSeek = null;
        }
        return;
      }

      // Recomeçar depois do fim avisa o fim de novo (o quiz volta a abrir).
      if (message.event === 'play') {
        playback.firedEnded = false;
        return;
      }

      if (message.event === 'ended' && !playback.firedEnded) {
        playback.firedEnded = true;
        // Fim sem ter passado pela marca (telemetria perdida): conclui antes.
        if (!playback.firedVirtual) {
          playback.firedVirtual = true;
          callbacks.onFinish && callbacks.onFinish('virtual');
        }
        callbacks.onFinish && callbacks.onFinish('ended');
        callbacks.onVideoEnd && callbacks.onVideoEnd();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [playerOrigin]);

  // Tópicos da videoaula: o requestId garante o salto mesmo ao clicar duas
  // vezes no mesmo item. Antes do Player ficar pronto, o salto espera.
  useEffect(() => {
    if (!seekTo || !Number.isFinite(seekTo.seconds)) return;
    const seconds = Math.max(0, seekTo.seconds);
    if (playbackRef.current.ready) {
      sendRef.current({ command: 'seek', seconds });
    } else {
      playbackRef.current.pendingSeek = seconds;
    }
  }, [seekTo?.requestId]);

  // Atalhos com o foco na página (como no YouTube). Com o foco dentro do
  // iframe, o próprio Player trata as teclas e a página nem as recebe.
  useEffect(() => {
    if (!keyboardShortcuts || !embedUrl) return;
    const container = containerRef.current;
    if (!container) return;

    let visible = false;
    const observer =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            ([entry]) => {
              visible = Boolean(entry) && entry.intersectionRatio >= 0.5;
            },
            { threshold: [0, 0.5, 1] }
          )
        : null;
    if (observer) observer.observe(container);
    else visible = true;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!visible && document.fullscreenElement !== iframeRef.current) return;
      // Modal aberto por cima da aula: as teclas são dele.
      if (document.querySelector('[aria-modal="true"]')) return;

      const shortcut = shortcutFromKey(event, container);
      if (!shortcut) return;
      event.preventDefault();

      if (shortcut === 'fullscreen') {
        // Safari antigo não devolve Promise aqui.
        const request = document.fullscreenElement
          ? document.exitFullscreen?.()
          : iframeRef.current?.requestFullscreen?.();
        request?.catch?.(() => {});
        return;
      }

      sendRef.current(shortcut);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      observer?.disconnect();
    };
  }, [keyboardShortcuts, embedUrl]);

  if (!embedUrl) {
    return (
      <div
        className={`flex h-[465px] w-full items-center justify-center bg-black ${className ?? ''}`}
      >
        <p className='text-mono-color-white text-sm'>
          Vídeo indisponível no momento.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative h-[465px] w-full overflow-hidden bg-black ${className ?? ''}`}
      aria-busy={!loaded}
    >
      {/* embedUrl já vem do backend com as credenciais no fragmento — não modificar. */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={`Player: ${title}`}
        allow='autoplay; fullscreen; picture-in-picture'
        allowFullScreen
        referrerPolicy='no-referrer'
        scrolling='no'
        onLoad={() => setLoaded(true)}
        className='absolute inset-0 h-full w-full border-0 bg-black'
      />
    </div>
  );
}
